import { normalizeCatalogTag } from './normalizeCatalogTag'
import { getOrgPinnedTagGroupNameFromMemory, getTagByIdFromMemory } from './tagsCatalogMemory'

const toTagId = (value) => {
  if (value == null) return ''
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim()
  return String(value?.tag_id ?? value?.id ?? value?.value ?? '').trim()
}

const isUuidString = (value) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim())

/**
 * Read tag ids from shipment payloads during the CC-6037 dual-read window.
 * Prefers `tag_ids` (shipments table column); falls back to RPC `assigned_tags`.
 * @param {object | null | undefined} shipment
 * @returns {string[]} Tag UUIDs in display order when available.
 */
export function getShipmentTagIds(shipment) {
  if (!shipment) return []

  if (Array.isArray(shipment.tag_ids)) {
    return shipment.tag_ids.map(toTagId).filter(Boolean)
  }

  if (Array.isArray(shipment.shipment_assigned_tags)) {
    return shipment.shipment_assigned_tags.map(toTagId).filter(Boolean)
  }

  if (Array.isArray(shipment.assigned_tags)) {
    return shipment.assigned_tags.map(toTagId).filter(Boolean)
  }

  return []
}

const buildLegacyTagLookup = (shipment) => {
  const legacyById = new Map()
  if (!Array.isArray(shipment?.assigned_tags)) return legacyById

  shipment.assigned_tags.forEach((tag) => {
    const id = toTagId(tag)
    if (id) legacyById.set(id, tag)
  })

  return legacyById
}

const applyPinnedTagFlag = (resolved, legacyTag, pinnedGroupName) => {
  if (!resolved) return resolved

  if (legacyTag?.pinned_tag != null) {
    resolved.pinned_tag = Boolean(legacyTag.pinned_tag)
    return resolved
  }

  if (resolved.pinned === true) {
    resolved.pinned_tag = true
    return resolved
  }

  const groupName = resolved.group_name
  if (pinnedGroupName && groupName && groupName === pinnedGroupName) {
    resolved.pinned = true
    resolved.pinned_tag = true
  }

  return resolved
}

/**
 * Resolve shipment tag UUIDs to display metadata from the org tags catalog.
 * Falls back to embedded legacy `assigned_tags` objects when catalog metadata is missing.
 * Tags with no org-catalog match and no legacy metadata are omitted from display.
 * When cc-6037 catalog hydration is active, tags in the org pinned tag group get `pinned_tag`.
 * @param {object | null | undefined} shipment
 * @param {string | null | undefined} orgId
 * @returns {Array<object>} Resolved TagMeta entries for UI display.
 */
export function resolveShipmentAssignedTags(shipment, orgId) {
  const legacyById = buildLegacyTagLookup(shipment)
  const tagIds = getShipmentTagIds(shipment)
  const pinnedGroupName = orgId ? getOrgPinnedTagGroupNameFromMemory(orgId) : null

  if (tagIds.length === 0 && legacyById.size === 0) {
    return []
  }

  const orderedIds = tagIds.length > 0 ? tagIds : Array.from(legacyById.keys())

  return orderedIds
    .map((tagId) => {
      const fromCatalog = orgId ? getTagByIdFromMemory(orgId, tagId) : null
      const legacyTag = legacyById.get(tagId)

      if (!fromCatalog) {
        if (!legacyTag) {
          return null
        }

        const legacyResolved = normalizeCatalogTag(legacyTag)
        if (!legacyResolved || isUuidString(legacyResolved.tag_name)) {
          return null
        }

        return applyPinnedTagFlag(legacyResolved, legacyTag, pinnedGroupName)
      }

      const resolved = { ...fromCatalog }

      if (!resolved) {
        return null
      }

      if (legacyTag?.pinned != null) {
        resolved.pinned = Boolean(legacyTag.pinned)
      }

      return applyPinnedTagFlag(resolved, legacyTag, pinnedGroupName)
    })
    .filter(Boolean)
}
